/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { memo } from "react";
import type { IconProps } from "./types";

interface PlayCircleIconProps extends IconProps {
  /** Background circle fill color */
  bgFill?: string;
  /** Background circle fill opacity */
  bgOpacity?: string;
  /** Play triangle fill color */
  playFill?: string;
  /** Play triangle fill opacity */
  playOpacity?: string;
}

/** Play button circle overlay for video thumbnails */
export const PlayCircleIcon: React.FC<PlayCircleIconProps> = memo(
  ({
    width = "58",
    height = "58",
    className,
    bgFill = "white",
    bgOpacity = "0.6",
    playFill = "black",
    playOpacity = "0.6",
  }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 58 58"
      fill="none"
      className={className}
    >
      <circle cx="29" cy="29" r="29" fill={bgFill} fillOpacity={bgOpacity} />
      <path
        d="M40.3984 25.536C43.0651 27.0756 43.0651 30.9246 40.3984 32.4642L26.2984 40.6048C23.6318 42.1444 20.2984 40.2199 20.2984 37.1407L20.2984 20.8595C20.2984 17.7803 23.6318 15.8558 26.2984 17.3954L40.3984 25.536Z"
        fill={playFill}
        fillOpacity={playOpacity}
      />
    </svg>
  ),
);
PlayCircleIcon.displayName = "PlayCircleIcon";

/** Camera/screenshot icon — image with scenery */
export const ScreenCaptureIcon: React.FC<IconProps> = memo(
  ({ width = "20", height = "20", className }) => (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M16.25 3.125H3.75C2.71447 3.125 1.875 3.96447 1.875 5V15C1.875 16.0355 2.71447 16.875 3.75 16.875H16.25C17.2855 16.875 18.125 16.0355 18.125 15V5C18.125 3.96447 17.2855 3.125 16.25 3.125Z"
        stroke="#7C7C7C"
        strokeLinejoin="round"
      />
      <path
        d="M13.125 8.125C13.8154 8.125 14.375 7.56536 14.375 6.875C14.375 6.18464 13.8154 5.625 13.125 5.625C12.4346 5.625 11.875 6.18464 11.875 6.875C11.875 7.56536 12.4346 8.125 13.125 8.125Z"
        stroke="#7C7C7C"
        strokeMiterlimit="10"
      />
      <path
        d="M11.875 13.1168L8.33359 9.58202C8.10824 9.35672 7.80535 9.22599 7.48684 9.21655C7.16832 9.20711 6.85822 9.31968 6.61992 9.53124L1.875 13.75M8.75 16.875L13.568 12.057C13.7883 11.8362 14.0832 11.7058 14.3948 11.6914C14.7064 11.6769 15.0121 11.7795 15.252 11.9789L18.125 14.375"
        stroke="#7C7C7C"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
);
ScreenCaptureIcon.displayName = "ScreenCaptureIcon";

/** Calendar icon for date pickers */
export const CalendarIcon: React.FC<IconProps> = memo(
  ({ width = "20", height = "20", className }) => (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M16.9998 4H2.99976C1.89519 4 0.999756 4.89543 0.999756 6V17C0.999756 18.1046 1.89519 19 2.99976 19H16.9998C18.1043 19 18.9998 18.1046 18.9998 17V6C18.9998 4.89543 18.1043 4 16.9998 4Z"
        stroke="#535353"
        strokeWidth="2"
      />
      <path
        d="M0.999756 8C0.999756 6.114 0.999756 5.172 1.58576 4.586C2.17176 4 3.11376 4 4.99976 4H14.9998C16.8858 4 17.8278 4 18.4138 4.586C18.9998 5.172 18.9998 6.114 18.9998 8H0.999756Z"
        fill="#535353"
      />
      <path
        d="M4.99976 1V4M14.9998 1V4"
        stroke="#535353"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  ),
);
CalendarIcon.displayName = "CalendarIcon";
